import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { videosApi } from "../../lib/api/videos";
import { errorMessage } from "../../lib/errors";
import { uploadMultipart, type UploadPorts } from "../../lib/upload/multipart";
import { videoContentType } from "../../lib/upload/content-type";
import { xhrPut } from "../../lib/upload/xhr-put";

export type UploadStatus = "queued" | "uploading" | "done" | "failed" | "cancelled";

export interface UploadItem {
  key: string;
  workspaceId: string;
  name: string;
  sent: number;
  total: number;
  status: UploadStatus;
  error?: string;
}

interface UploadsValue {
  items: UploadItem[];
  add: (workspaceId: string, files: File[]) => void;
  cancel: (key: string) => void;
  clearFinished: () => void;
  /** Bumps whenever an upload finishes, so the library knows to reload. */
  finishedCount: number;
}

const UploadsContext = createContext<UploadsValue | null>(null);

const portsFor = (workspaceId: string): UploadPorts => ({
  init: (f) => videosApi.initUpload(workspaceId, f),
  partUrls: videosApi.partUrls,
  putPart: xhrPut,
  complete: videosApi.complete,
  abort: videosApi.abort,
});

/**
 * Upload queue that lives above the pages, so moving around the app doesn't cancel an upload. One file at a time;
 * each file sends several parts in parallel.
 */
export function UploadsProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [finishedCount, setFinished] = useState(0);
  const [tick, setTick] = useState(0);
  const files = useRef(new Map<string, File>());
  const controllers = useRef(new Map<string, AbortController>());
  const running = useRef(false);

  const patch = (key: string, p: Partial<UploadItem>) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const pump = useCallback(async (queue: UploadItem[]) => {
    if (running.current) return;
    const next = queue.find((x) => x.status === "queued");
    if (!next) return;
    running.current = true;
    const file = files.current.get(next.key)!;
    const ctrl = new AbortController();
    controllers.current.set(next.key, ctrl);
    patch(next.key, { status: "uploading" });
    try {
      const contentType = videoContentType(file);
      if (!contentType) throw new Error("That file type isn't supported. Use MP4, MOV, MKV, WebM, AVI, MPEG or TS.");
      await uploadMultipart(file, portsFor(next.workspaceId), {
        contentType,
        signal: ctrl.signal,
        onProgress: (sent, total) => patch(next.key, { sent, total }),
      });
      patch(next.key, { status: "done", sent: file.size });
      setFinished((n) => n + 1);
    } catch (e) {
      patch(next.key, ctrl.signal.aborted ? { status: "cancelled" } : { status: "failed", error: errorMessage(e) });
    } finally {
      files.current.delete(next.key);
      controllers.current.delete(next.key);
      running.current = false;
      setTick((t) => t + 1); // look for the next queued file
    }
  }, []);

  useEffect(() => { void pump(items); }, [items, tick, pump]);

  // Leaving the site mid-upload loses it; ask first.
  const active = items.some((x) => x.status === "queued" || x.status === "uploading");
  useEffect(() => {
    if (!active) return;
    const h = (e: BeforeUnloadEvent) => e.preventDefault();
    addEventListener("beforeunload", h);
    return () => removeEventListener("beforeunload", h);
  }, [active]);

  const value = useMemo<UploadsValue>(() => ({
    items,
    finishedCount,
    add: (workspaceId, list) => {
      const added = list.map((f) => {
        const key = crypto.randomUUID();
        files.current.set(key, f);
        return { key, workspaceId, name: f.name, sent: 0, total: f.size, status: "queued" as const };
      });
      setItems((xs) => [...xs, ...added]);
    },
    cancel: (key) => {
      const c = controllers.current.get(key);
      if (c) c.abort();
      else { files.current.delete(key); patch(key, { status: "cancelled" }); }
    },
    clearFinished: () => setItems((xs) => xs.filter((x) => x.status === "queued" || x.status === "uploading")),
  }), [items, finishedCount]);

  return <UploadsContext.Provider value={value}>{children}</UploadsContext.Provider>;
}

export function useUploads(): UploadsValue {
  const v = useContext(UploadsContext);
  if (!v) throw new Error("useUploads outside UploadsProvider");
  return v;
}
